import React from 'react';

interface StateType {
  open: boolean;
  fullscreen: boolean;
  content: React.ReactElement;
  shouldClose: boolean;
}

const emptyState = { open: false, shouldClose: false, fullscreen: false, content: <div /> };

function useModal() {
  const [modalState, setModalState] = React.useState<StateType>(emptyState);
  const [disableQuickClose, setDisableQuickClose] = React.useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = React.useState(false);

  React.useEffect(() => {
    if (modalState.shouldClose) {
      setModalState(emptyState);
      setDisableQuickClose(false);
      setShowCloseConfirmation(false);
    }
  }, [modalState]);

  const openModal = React.useCallback((content: React.ReactElement, fullscreen = false) => {
    setModalState({ open: true, shouldClose: false, content, fullscreen });
    setDisableQuickClose(false);
    setShowCloseConfirmation(false);
  }, []);

  const closeModal = React.useCallback(() => {
    setModalState((state) => ({ ...state, shouldClose: true }));
  }, []);

  const requestClose = React.useCallback(() => {
    if (disableQuickClose) {
      setShowCloseConfirmation(true);
    } else {
      closeModal();
    }
  }, [disableQuickClose, closeModal]);

  const cancelCloseConfirmation = React.useCallback(() => {
    setShowCloseConfirmation(false);
  }, []);

  const { open, content, fullscreen } = modalState;

  return React.useMemo(
    () => ({
      openModal,
      closeModal,
      requestClose,
      open,
      content,
      fullscreen,
      disableQuickClose,
      setDisableQuickClose,
      showCloseConfirmation,
      cancelCloseConfirmation,
    }),
    [openModal, closeModal, requestClose, open, content, fullscreen, disableQuickClose, showCloseConfirmation, cancelCloseConfirmation]
  );
}

export default useModal;
